/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { useEffect, useRef, useState } from 'react'
import { useDeviceMediaQuery } from '../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'
import { useTranslation } from 'react-i18next'

const PATH_COLOR = 'lightgrey'
const ARROW_SIZE = 10

const SnakePath = ({ aisles = [] }) => {
    const [t] = useTranslation()
    const canvasRef = useRef(null)
    const containerRef = useRef(null)
    const resizeObserverRef = useRef(null)
    const [pathEndpoints, setPathEndpoints] = useState({
        start: { x: 0, y: 0, visible: false },
        end: { x: 0, y: 0, visible: false }
    })
    const { isMobile, isTablet } = useDeviceMediaQuery()

    const PATH_WIDTH = isMobile ? 8 : 10
    const START_RECT_WIDTH = isMobile ? 90 : 100
    const START_RECT_HEIGHT = isMobile ? 45 : 50
    const END_RECT_WIDTH = START_RECT_WIDTH
    const END_RECT_HEIGHT = START_RECT_HEIGHT
    const ITEMS_PER_ROW = isMobile ? 1 : (isTablet ? 3 : 5)

    const getPositionFromElement = (element) => {
        const positionElement = element.querySelector('[data-position]') ||
                               element.querySelector('div > div:first-of-type');
        if (positionElement) {
            const posAttr = positionElement.getAttribute('data-position');
            if (posAttr) {
                return parseInt(posAttr, 10);
            }

            const positionText = positionElement.textContent.trim();
            const parsedPosition = parseInt(positionText, 10);
            if (!isNaN(parsedPosition)) {
                return parsedPosition;
            }
        }
        return 0;
    }

    const drawPath = () => {
        if (!canvasRef.current || aisles.length === 0 || !containerRef.current) return

        const canvas = canvasRef.current
        const ctx = canvas.getContext('2d')

        const parentContainer = containerRef.current.closest('.draggable-list-container')
        if (!parentContainer) return

        const gridItems = parentContainer.querySelectorAll('.grid-item:not(.empty-slot):not(.withdrawn-item)')

        if (gridItems.length === 0) return

        const container = containerRef.current
        canvas.width = container.offsetWidth
        canvas.height = container.offsetHeight

        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.strokeStyle = PATH_COLOR
        ctx.lineWidth = PATH_WIDTH
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'

        const sortedItems = Array.from(gridItems)
            .filter(item => !item.classList.contains('empty-slot'))
            .map(item => ({
                element: item,
                position: getPositionFromElement(item)
            }))
            .sort((a, b) => a.position - b.position)
            .map(item => item.element);

        const positions = sortedItems.map(item => {
            const rect = item.getBoundingClientRect()
            const canvasRect = canvas.getBoundingClientRect()
            return {
                x: rect.x - canvasRect.x + rect.width / 2,
                y: rect.y - canvasRect.y + rect.height / 2,
                width: rect.width,
                height: rect.height,
                position: getPositionFromElement(item)
            }
        });

        if (positions.length === 0) return

        const startPoint = {
            x: positions[0].x,
            y: positions[0].y - positions[0].height / 2 - START_RECT_HEIGHT / 2 - 5
        };

        ctx.beginPath();

        ctx.moveTo(startPoint.x, startPoint.y + START_RECT_HEIGHT / 2);

        ctx.lineTo(positions[0].x, positions[0].y - positions[0].height / 2);
        ctx.lineTo(positions[0].x, positions[0].y);

        for (let i = 0; i < positions.length - 1; i++) {
            const currentPos = positions[i];
            const nextPos = positions[i + 1];
            const currentPosNum = currentPos.position;
            const nextPosNum = nextPos.position;

            const isRowCrossing =
                Math.floor((currentPosNum - 1) / ITEMS_PER_ROW) !==
                Math.floor((nextPosNum - 1) / ITEMS_PER_ROW);

            if (isRowCrossing) {
                const midY = currentPos.y + (nextPos.y - currentPos.y) / 2;
                ctx.lineTo(currentPos.x, midY);
                ctx.lineTo(nextPos.x, midY);
                ctx.lineTo(nextPos.x, nextPos.y);
            } else {
                ctx.lineTo(nextPos.x, nextPos.y);
            }
        }

        if (positions.length > 0) {
            const lastPos = positions[positions.length - 1];
            const arrowY = lastPos.y + lastPos.height / 2 + 20;
            ctx.lineTo(lastPos.x, arrowY - ARROW_SIZE);
            ctx.stroke();

            setPathEndpoints({
                start: {
                    x: startPoint.x,
                    y: startPoint.y,
                    visible: true
                },
                end: {
                    x: lastPos.x,
                    y: arrowY,
                    visible: true
                }
            });
        }
    }

    useEffect(() => {
        if (resizeObserverRef.current) {
            resizeObserverRef.current.disconnect();
        }

        resizeObserverRef.current = new ResizeObserver(() => drawPath());

        if (containerRef.current) {
            resizeObserverRef.current.observe(containerRef.current);
        }

        setTimeout(drawPath, 150);
        window.addEventListener('resize', drawPath);

        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
            window.removeEventListener('resize', drawPath);
        };
    }, [aisles, ITEMS_PER_ROW]);

    return (
        <div
            ref={containerRef}
            css={css`
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                pointer-events: none;
                z-index: 0;
            `}
        >
            <canvas
                ref={canvasRef}
                css={css`
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                `}
            />
            {pathEndpoints.start.visible && (
                <div
                    css={css`
                        position: absolute;
                        top: ${pathEndpoints.start.y - START_RECT_HEIGHT / 2}px;
                        left: ${pathEndpoints.start.x - START_RECT_WIDTH / 2}px;
                        width: ${START_RECT_WIDTH}px;
                        height: ${START_RECT_HEIGHT}px;
                        border: 1px solid grey;
                        background-color: white;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-weight: bold;
                        font-size: .9rem;
                    `}
                >
                    {t('picking_path.start')}
                </div>
            )}
            {pathEndpoints.end.visible && (
                <>
                    <div
                        css={css`
                            position: absolute;
                            top: ${pathEndpoints.end.y - ARROW_SIZE}px;
                            left: ${pathEndpoints.end.x - ARROW_SIZE}px;
                            width: 0;
                            height: 0;
                            border-left: ${ARROW_SIZE}px solid transparent;
                            border-right: ${ARROW_SIZE}px solid transparent;
                            border-top: ${ARROW_SIZE * 1.5}px solid ${PATH_COLOR};
                            z-index: 1;
                        `}
                    />
                    <div
                        css={css`
                            position: absolute;
                            top: ${pathEndpoints.end.y + ARROW_SIZE / 2}px;
                            left: ${pathEndpoints.end.x - END_RECT_WIDTH / 2}px;
                            width: ${END_RECT_WIDTH}px;
                            height: ${END_RECT_HEIGHT}px;
                            border: 1px solid grey;
                            background-color: white;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: bold;
                            font-size: .9rem;
                        `}
                    >
                        {t('picking_path.end')}
                    </div>
                </>
            )}
        </div>
    )
}

export { SnakePath }
